import { Box, SxProps, Theme } from "@mui/material";
import { ArrowLink } from "src/components-mui/base/arrow-link/arrow-link";
import { OptionalLink } from "src/components-mui/base/optional-link/optional-link";
import { ResponsiveImage } from "src/components-mui/base/responsive-image/responsive-image";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { ModulePrimaryHeadline } from "src/components-mui/common/module-headlines";
import { SimpleRichText } from "src/components-mui/common/rich-text/simple-rich-text";
import { ScrollSnap, ScrollSnapItem } from "src/components-mui/common/scroll-snap";
import { Container } from "src/components-mui/layout/container";
import { IImage, ILink } from "src/types/common";
import { EnumHorizontalAlignment } from "src/types/common-enum";
import { ISrc } from "src/utils/responsive-image-util";

const ITEM_IMAGE_SRC_SET: ISrc[] = [
    { queryParams: { w: 312, h: 208, fit: "crop" } },
    { queryParams: { w: 315, h: 210, fit: "crop" } },
    { queryParams: { w: 291, h: 194, fit: "crop" } },
    { queryParams: { w: 358, h: 239, fit: "crop" } },
];

const ITEM_LINK_SX: SxProps<Theme> = {
    display: "contents",
    fontSize: "inherit",
    "&:hover h3": {
        textDecoration: "underline",
    },
};

const ITEM_IMAGE_SX: SxProps<Theme> = {
    display: "block",
    width: "100%",
    aspectRatio: "3 / 2",
    objectFit: "cover",
    borderRadius: 1,
    mb: 2,
};

const ITEM_TITLE_SX: SxProps<Theme> = { mb: 1 };

const ITEM_DESCRIPTION_SX: SxProps<Theme> = { mb: 2 };

const DESCRIPTION_SX: SxProps<Theme> = {
    maxWidth: { xs: "100%", sm: "582px", md: "775px", lg: "929px" },
};

const CENTERED_DESCRIPTION_SX: SxProps<Theme> = { mx: "auto" };

const ITEMS_GRID_SX: SxProps<Theme> = {
    rowGap: { xs: 2, sm: 3, md: 5 },
    overflowX: { xs: "auto", sm: "visible" },
};

const LINK_WRAPPER_SX: SxProps<Theme> = {
    display: "flex",
    flexDirection: { xs: "row", md: "row-reverse" },
};

const ARROW_LINK_SX: SxProps<Theme> = {
    display: "inline-block",
    mt: { xs: 2, sm: 3, md: 4 },
    fontSize: "md",
};

interface IPreviewAdvantagesItem {
    title: string;
    image: IImage;
    description?: string;
    link?: ILink;
}

const PreviewAdvantagesItem = ({ title, description, link, image }: IPreviewAdvantagesItem) => (
    <OptionalLink href={link?.url} target={link?.target} variant="unstyled" sx={ITEM_LINK_SX}>
        <Box>
            <ResponsiveImage baseUrl={image.url} alt={image.alt} srcSet={ITEM_IMAGE_SRC_SET} sx={ITEM_IMAGE_SX} />
            <WfHeading component="h3" fontSize="xl" sx={ITEM_TITLE_SX}>
                {title}
            </WfHeading>
            {description && <SimpleRichText text={description} sx={ITEM_DESCRIPTION_SX} />}
        </Box>
    </OptionalLink>
);

export interface IPreviewAdvantagesModuleProps {
    title: string;
    description?: string;
    titleAsH1: boolean;
    headerAlignment: EnumHorizontalAlignment;
    link?: ILink;
    items: IPreviewAdvantagesItem[];
    columnsDesktop?: number;
    enableHorizontalScrolling?: boolean;
}

export const PreviewAdvantagesModule = ({
    title,
    description,
    titleAsH1,
    headerAlignment,
    link,
    items,
    columnsDesktop = 4,
    enableHorizontalScrolling = false,
}: IPreviewAdvantagesModuleProps) => {
    const hasThreeItems = items.length === 3;
    const isCentered = headerAlignment !== EnumHorizontalAlignment.Left;

    return (
        <Container>
            {(title || description) && (
                <Box sx={{ textAlign: isCentered ? "center" : "left", mb: description ? 5 : { xs: 5, md: 7 } }}>
                    {title && (
                        <ModulePrimaryHeadline titleAsH1={titleAsH1} sx={{ mb: description ? 2 : 0 }}>
                            {title}
                        </ModulePrimaryHeadline>
                    )}
                    {description && (
                        <SimpleRichText
                            text={description}
                            fontSize="lg"
                            align={isCentered ? "center" : "left"}
                            sx={[DESCRIPTION_SX, isCentered && CENTERED_DESCRIPTION_SX]}
                        />
                    )}
                </Box>
            )}
            <ScrollSnap
                sx={[
                    ITEMS_GRID_SX,
                    {
                        columnGap: { xs: 2, sm: hasThreeItems ? 2 : 5, md: 3 },
                        gridTemplateColumns: {
                            xs: `repeat(${enableHorizontalScrolling ? items.length + 1 : 1}, auto)`,
                            sm: `repeat(${hasThreeItems ? 3 : 2}, minmax(0, 1fr))`,
                            md: `repeat(${columnsDesktop}, minmax(0, 1fr))`,
                        },
                    },
                ]}
            >
                {items.map(itemProps => (
                    <ScrollSnapItem key={itemProps.title} sx={{ width: { xs: enableHorizontalScrolling ? "288px" : "auto", sm: "auto" } }}>
                        <PreviewAdvantagesItem {...itemProps} />
                    </ScrollSnapItem>
                ))}
            </ScrollSnap>

            {link && (
                <Box sx={LINK_WRAPPER_SX}>
                    <ArrowLink href={link.url} target={link.target} color="green" sx={ARROW_LINK_SX}>
                        {link.name}
                    </ArrowLink>
                </Box>
            )}
        </Container>
    );
};
